import {
  Component,
  Input,
  HostBinding,
  ElementRef,
  Output,
  EventEmitter,
  DestroyRef,
  AfterViewInit,
  ViewChildren,
  QueryList,
  ChangeDetectorRef,
} from '@angular/core';
import { MenuAction, SubMenuActions } from '../../types';
import {
  Observable,
  combineLatest,
  from,
  map,
  startWith,
  ReplaySubject,
  delay,
} from 'rxjs';
import { UntypedFormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { WindowHelperService } from '@anecdotes/utils';
import { PositionClass, SubmenuDirection } from '../../models';
import { POSITION_MAPPING } from '../../constants';

@Component({
  selector: 'app-dropdown-option-with-submenu',
  templateUrl: './dropdown-option-with-submenu.component.html',
  styleUrls: ['./dropdown-option-with-submenu.component.scss'],
  standalone: false,
})
export class DropdownOptionWithSubmenuComponent implements AfterViewInit {
  @ViewChildren('submenu')
  private readonly submenuList: QueryList<ElementRef<HTMLDivElement>>;

  @HostBinding('class.sub-menu-shown')
  private submenuShown = false;

  private submenuElement: ElementRef<HTMLDivElement> | undefined;

  private _menuAction: MenuAction;

  private readonly submenuInitialization$ = new ReplaySubject<void>(1);

  private get hostDomRect(): DOMRect {
    return this.elementRef.nativeElement.getBoundingClientRect();
  }

  private get submenuDomRect(): DOMRect | undefined {
    return this.submenuElement.nativeElement.getBoundingClientRect();
  }

  private get responsiveLeftPosition(): PositionClass {
    if (!this.submenuElement) {
      return;
    }

    return this.hostDomRect.top > this.submenuDomRect.height ? POSITION_MAPPING.leftTop : POSITION_MAPPING.leftBottom;
  }

  private get responsiveRightPosition(): PositionClass {
    if (!this.submenuElement) {
      return;
    }

    return this.hostDomRect.top > this.submenuDomRect.height ? POSITION_MAPPING.rightTop : POSITION_MAPPING.rightBottom;
  }

  readonly searchField = new UntypedFormControl('');

  displayedData$: Observable<MenuAction[]>;

  @Input()
  set menuAction(value: MenuAction) {
    this._menuAction = value;

    if (this._menuAction && this._menuAction.subMenuFactory) {
      this.subMenuActions$ = from(this._menuAction.subMenuFactory());
      this.initDataListener();
      this.submenuInitialization$.next();
    }
  }

  @Input()
  context: any;

  @Input()
  selected: boolean;

  @Output()
  readonly nestedDropdownClick = new EventEmitter<void>();

  subMenuActions$: Observable<SubMenuActions<any>>;

  get shouldDisableOption(): boolean {
    if (this.menuAction?.disabledCondition) {
      return this.menuAction?.disabledCondition() || this.menuAction?.disabled;
    }
    return this.menuAction?.disabled;
  }

  get menuAction(): MenuAction {
    return this._menuAction;
  }

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly translateService: TranslateService,
    private readonly destroyRef: DestroyRef,
    private readonly windowHelperService: WindowHelperService,
    private readonly cd: ChangeDetectorRef,
  ) {}

  ngAfterViewInit(): void {
    this.initializeSubmenuListener();
  }

  activateMenu(): void {
    this.submenuShown = true;
  }

  handleClick(event: MouseEvent): void {
    if (this.shouldDisableOption || this.menuAction?.actAsNotAnAction) {
      event.preventDefault();
    } else {
      this.nestedClick();
    }
  }

  nestedClick(): void {
    this.nestedDropdownClick.emit();
  }

  getSubmenuPositionClass(position: SubMenuActions['position'] | undefined): PositionClass | undefined {
    switch (position) {
      case 'left-top':
        return POSITION_MAPPING.leftTop;
      case 'left-bottom':
        return POSITION_MAPPING.leftBottom;
      case 'left':
        /* If there is not enough space on the left side, place the menu on the right */
        return this.responsiveXAxis(SubmenuDirection.left) === SubmenuDirection.left
          ? this.responsiveLeftPosition
          : this.responsiveRightPosition;
      case 'right-top':
        return POSITION_MAPPING.rightTop;
      case 'right-bottom':
        return POSITION_MAPPING.rightBottom;
      case 'right':
        /* If there is not enough space on the right side, place the menu on the left */
        return this.responsiveXAxis(SubmenuDirection.right) === SubmenuDirection.right
          ? this.responsiveRightPosition
          : this.responsiveLeftPosition;
    }
  }

  private initDataListener(): void {
    if (this.menuAction?.searchEnabled) {
      this.displayedData$ = combineLatest([
        this.searchField.valueChanges.pipe(startWith('')),
        this.subMenuActions$.pipe(map((subMenu) => subMenu.menuActions || [])),
      ]).pipe(
        map(([value, actions]) => {
          if (value) {
            return actions.filter((action) =>
              this.translateService.instant(action.translationKey).toLowerCase().includes(value.toLowerCase()),
            );
          }

          return actions;
        }),
      );
    } else {
      this.displayedData$ = this.subMenuActions$.pipe(map((subMenu) => subMenu.menuActions || []));
    }
  }

  private initializeSubmenuListener(): void {
    this.submenuList.notifyOnChanges();

    this.submenuList.changes
      .pipe(
        /* Wait for submenu items rendering */
        delay(0),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.submenuElement = this.submenuList.first;
        this.cd.detectChanges();
      });
  }

  private responsiveXAxis(preferredSize: SubmenuDirection): SubmenuDirection {
    if (!this.submenuElement) {
      return;
    }

    const window = this.windowHelperService.getWindow();

    switch (preferredSize) {
      case SubmenuDirection.left: {
        return (window.innerWidth - this.hostDomRect.left) > this.submenuDomRect.width
          ? SubmenuDirection.left
          : SubmenuDirection.right;
      }
      case SubmenuDirection.right: {
        return (window.innerWidth - this.hostDomRect.right) > this.submenuDomRect.width
          ? SubmenuDirection.right
          : SubmenuDirection.left;
      }
    }
  }
}
